/**
 * /admin/parents — Admin parent CRUD landing (Fase 2 N-15).
 *
 * Thin wrapper around <ParentCrudPanel>. beforeLoad enforces admin RBAC and
 * redirects non-admins to their own home (mirror of /admin/students).
 */

import type { ReactNode } from 'react';
import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { freshSessionQuery } from '../../lib/api/queries.js';
import { ParentCrudPanel } from '../../components/admin/ParentCrudPanel.js';

export const Route = createFileRoute('/admin/parents')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role === 'tutor' && user.isApprovedTutor) {
      throw redirect({ to: '/dashboard' });
    }
    if (user.role === 'tutor' || user.kind === 'student') {
      throw redirect({ to: '/me' });
    }
    if (user.role !== 'admin') {
      throw redirect({ to: '/' });
    }
    return null;
  },
  component: AdminParentsPage,
});

function AdminParentsPage(): ReactNode {
  // fetchJson is a thin wrapper that drops cookies and parses JSON. We unwrap
  // the { ok, data } envelope so the Panel can access typed fields directly
  // (matches the behaviour of fetchApi for /api/auth/me etc.).
  const fetchJson = async <T,>(input: RequestInfo, init?: RequestInit): Promise<T> => {
    const res = await fetch(input, {
      ...init,
      credentials: 'include',
      headers: { ...(init?.headers ?? {}), accept: 'application/json' },
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: { message?: string } };
      throw new Error(body.error?.message ?? `HTTP ${res.status}`);
    }
    const body = (await res.json()) as { ok?: boolean; data?: T };
    if (body.ok === false) {
      throw new Error('Operasi tidak berhasil.');
    }
    return body.data as T;
  };

  return (
    <main className="page-shell">
      <PageHeader
        eyebrow="Manajemen"
        title="Manajemen orang tua"
        description="Buat akun orang tua, tautkan dengan siswa, dan kelola status aktif/nonaktif. Self-service flow (magic link) menyusul di Fase 6 (N-16)."
      />
      <ParentCrudPanel fetchJson={fetchJson} />
      <p className="footnote">
        Butuh orang tua yang onboard sendiri lewat magic link? Lihat{' '}
        <Link to="/admin" className="inline-link">
          roadmap Fase 6
        </Link>
        .
      </p>
    </main>
  );
}
